import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Plus, Search, CheckCircle2, AlertTriangle } from 'lucide-react';
import { fetchAdminUtilities, updateUtility, createUtility } from '../../lib/adminQueries';
import { draftOf, patchFromDraft, type Draft } from '../../lib/adminUtilityDraft';
import { AMI_STATUS_VALUES, AMI_STATUS_LABELS, AMI_STATUS_DESCRIPTIONS, normalizeAmiStatus, routesFor } from '../../lib/utilityRoutes';
import type { AmiStatus, UtilityRow } from '../../types/utility';

/**
 * Admin → Utilities. Every utility TrueLoad knows, with the data route and the
 * homeowner instructions editable in place. Adding a card for a utility the
 * demand queue surfaced is a data edit here, not a deploy.
 */

type StatusFilter = 'all' | AmiStatus | 'unverified';

const INPUT =
  'w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100';
const LABEL = 'block text-xs font-medium text-gray-600 mb-1';

function statusBadge(raw: string) {
  const status = normalizeAmiStatus(raw);
  const tone: Record<AmiStatus, string> = {
    email_request: 'bg-blue-100 text-blue-800',
    self_download: 'bg-green-100 text-green-800',
    customer_request: 'bg-teal-100 text-teal-800',
    connect: 'bg-purple-100 text-purple-800',
    none: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${tone[status]}`}>
      {AMI_STATUS_LABELS[status]}
      {raw !== status ? ` (${raw})` : ''}
    </span>
  );
}

export default function AdminUtilities() {
  const [rows, setRows] = useState<UtilityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newState, setNewState] = useState('');
  const [newStatus, setNewStatus] = useState<AmiStatus>('self_download');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchAdminUtilities());
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const states = useMemo(
    () => Array.from(new Set(rows.map(r => (r.state ?? '').toUpperCase()).filter(Boolean))).sort(),
    [rows]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (stateFilter !== 'all' && (r.state ?? '').toUpperCase() !== stateFilter) return false;
      const status = normalizeAmiStatus(r.ami_status);
      if (statusFilter === 'unverified') {
        if (!routesFor(r).hasInstructions || r.instructions_verified) return false;
      } else if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (q && !r.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, stateFilter, statusFilter]);

  const counts = useMemo(() => {
    const c: Record<AmiStatus, number> = { email_request: 0, self_download: 0, customer_request: 0, connect: 0, none: 0 };
    let unverified = 0;
    for (const r of rows) {
      c[normalizeAmiStatus(r.ami_status)] += 1;
      if (routesFor(r).hasInstructions && !r.instructions_verified) unverified += 1;
    }
    return { ...c, unverified };
  }, [rows]);

  function startEdit(u: UtilityRow) {
    setEditingId(u.id);
    setDraft(draftOf(u));
    setSaveError(null);
    setSavedAt(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(null);
    setSaveError(null);
  }

  async function save() {
    if (!editingId || !draft) return;
    const original = rows.find(r => r.id === editingId);
    if (!original) return;
    const { patch, error: draftError } = patchFromDraft(original, draft);
    if (draftError) {
      setSaveError(draftError);
      return;
    }
    if (Object.keys(patch).length === 0) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateUtility(editingId, patch);
      setRows(prev => prev.map(r => (r.id === editingId ? updated : r)));
      setDraft(draftOf(updated));
      setSavedAt(Date.now());
    } catch (e) {
      setSaveError(String((e as { message?: string })?.message ?? e));
    } finally {
      setSaving(false);
    }
  }

  async function clearFeedback(u: UtilityRow) {
    setSaving(true);
    try {
      const updated = await updateUtility(u.id, { instructions_feedback: null });
      setRows(prev => prev.map(r => (r.id === u.id ? updated : r)));
    } catch (e) {
      setSaveError(String((e as { message?: string })?.message ?? e));
    } finally {
      setSaving(false);
    }
  }

  async function addUtility() {
    const name = newName.trim();
    const state = newState.trim().toUpperCase();
    if (!name || !/^[A-Z]{2}$/.test(state)) {
      setSaveError('A new utility needs a name and a two-letter state.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const created = await createUtility({ name, state, ami_status: newStatus });
      setRows(prev => [...prev, created]);
      setAdding(false);
      setNewName('');
      setNewState('');
      startEdit(created);
    } catch (e) {
      setSaveError(String((e as { message?: string })?.message ?? e));
    } finally {
      setSaving(false);
    }
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft(d => (d ? { ...d, [key]: value } : d));

  return (
    <div data-testid="admin-utilities">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Utilities</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {rows.length} utilities · {counts.self_download} self-download · {counts.email_request} email request · {counts.customer_request} customer request ·{' '}
            {counts.connect} connect · {counts.none} no route · {counts.unverified} with unverified steps
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setAdding(a => !a); setSaveError(null); }}
            data-testid="admin-utility-add"
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white hover:bg-purple-700 rounded-md"
          >
            <Plus className="w-4 h-4" /> Add utility
          </button>
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-sm text-red-700">{error}</div>
      )}

      {adding && (
        <div className="bg-white rounded-lg border border-purple-200 p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="admin-utility-add-form">
          <div className="sm:col-span-2">
            <label className={LABEL}>Name</label>
            <input value={newName} onChange={e => setNewName(e.target.value)} className={INPUT} placeholder="As it appears on the bill" />
          </div>
          <div>
            <label className={LABEL}>State</label>
            <input value={newState} onChange={e => setNewState(e.target.value)} className={INPUT} placeholder="e.g. TX" maxLength={2} />
          </div>
          <div>
            <label className={LABEL}>Route</label>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value as AmiStatus)} className={INPUT}>
              {AMI_STATUS_VALUES.map(v => (
                <option key={v} value={v}>{AMI_STATUS_LABELS[v]}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-4 flex gap-2">
            <button onClick={addUtility} disabled={saving} className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:bg-gray-300">
              {saving ? 'Adding…' : 'Add'}
            </button>
            <button onClick={() => setAdding(false)} className="px-4 py-2 text-sm text-gray-600 underline">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name"
            data-testid="admin-utility-search"
            className={`${INPUT} pl-8 w-64`}
          />
        </div>
        <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} className={`${INPUT} w-auto`} data-testid="admin-utility-state-filter">
          <option value="all">All states</option>
          {states.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as StatusFilter)} className={`${INPUT} w-auto`} data-testid="admin-utility-status-filter">
          <option value="all">All routes</option>
          {AMI_STATUS_VALUES.map(v => (
            <option key={v} value={v}>{AMI_STATUS_LABELS[v]}</option>
          ))}
          <option value="unverified">Unverified steps</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading utilities…</div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Utility', 'State', 'Route', 'Instructions', 'Contact', 'Customers'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wide text-xs whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">No utilities match.</td>
                  </tr>
                )}
                {visible.map(u => {
                  const isEditing = editingId === u.id;
                  const hasSteps = routesFor(u).hasInstructions;
                  return (
                    <RowAndEditor
                      key={u.id}
                      utility={u}
                      isEditing={isEditing}
                      hasSteps={hasSteps}
                      draft={isEditing ? draft : null}
                      saving={saving}
                      saveError={isEditing ? saveError : null}
                      savedAt={isEditing ? savedAt : null}
                      onEdit={() => startEdit(u)}
                      onCancel={cancelEdit}
                      onSave={save}
                      onClearFeedback={() => clearFeedback(u)}
                      set={set}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

interface RowProps {
  utility: UtilityRow;
  isEditing: boolean;
  hasSteps: boolean;
  draft: Draft | null;
  saving: boolean;
  saveError: string | null;
  savedAt: number | null;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onClearFeedback: () => void;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
}

function RowAndEditor({ utility: u, isEditing, hasSteps, draft, saving, saveError, savedAt, onEdit, onCancel, onSave, onClearFeedback, set }: RowProps) {
  return (
    <>
      <tr
        className={`hover:bg-gray-50 cursor-pointer ${isEditing ? 'bg-purple-50' : ''} ${u.is_active === false ? 'opacity-60' : ''}`}
        onClick={() => (isEditing ? onCancel() : onEdit())}
        data-testid="admin-utility-row"
      >
        <td className="px-4 py-3 font-medium text-gray-900">
          {u.name}
          {u.instructions_feedback && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700" title={u.instructions_feedback}>
              <AlertTriangle className="w-3.5 h-3.5" /> homeowner says the steps differed
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-gray-700">{u.state}</td>
        <td className="px-4 py-3">{statusBadge(u.ami_status)}</td>
        <td className="px-4 py-3 text-xs">
          {hasSteps ? (
            u.instructions_verified ? (
              <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> verified</span>
            ) : (
              <span className="text-amber-700">unverified</span>
            )
          ) : (
            <span className="text-gray-400">none</span>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-gray-600 break-all">{u.ami_contact_email || ''}</td>
        <td className="px-4 py-3 text-xs text-gray-600">{u.residential_customers ? u.residential_customers.toLocaleString() : ''}</td>
      </tr>
      {isEditing && draft && (
        <tr className="bg-purple-50/60">
          <td colSpan={6} className="px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-utility-editor">
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className={LABEL}>Name</label>
                    <input value={draft.name} onChange={e => set('name', e.target.value)} className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>State</label>
                    <input value={draft.state} onChange={e => set('state', e.target.value)} className={INPUT} maxLength={2} />
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Data route</label>
                  <select value={draft.ami_status} onChange={e => set('ami_status', e.target.value as AmiStatus)} className={INPUT} data-testid="admin-utility-status">
                    {AMI_STATUS_VALUES.map(v => (
                      <option key={v} value={v}>{AMI_STATUS_LABELS[v]}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">{AMI_STATUS_DESCRIPTIONS[draft.ami_status]}</p>
                </div>
                <div>
                  <label className={LABEL}>Contact email for data requests</label>
                  <input value={draft.ami_contact_email} onChange={e => set('ami_contact_email', e.target.value)} className={INPUT} placeholder="Leave blank if the utility has no address" />
                </div>
                <div>
                  <label className={LABEL}>Request form link (customer-request route)</label>
                  <textarea value={draft.request_form_template} onChange={e => set('request_form_template', e.target.value)} className={`${INPUT} min-h-[72px] font-mono text-xs`} placeholder="https://…?account={account}&startdate={startdate}…" data-testid="admin-utility-form-template" />
                  <p className="text-xs text-gray-500 mt-1">
                    Filled in for the customer: {'{account} {startdate} {enddate} {first_name} {last_name} {email} {phone} {address} {data_recipient_name} {data_recipient_email}'}. Anything the project lacks is left blank. Set, the customer submits this form instead of emailing.
                  </p>
                </div>
                <div>
                  <label className={LABEL}>Portal address</label>
                  <input value={draft.portal_url} onChange={e => set('portal_url', e.target.value)} className={INPUT} placeholder="https://" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL}>Export format</label>
                    <input value={draft.export_format} onChange={e => set('export_format', e.target.value)} className={INPUT} placeholder="Green Button XML or CSV" />
                  </div>
                  <div>
                    <label className={LABEL}>Months of history</label>
                    <input value={draft.history_months} onChange={e => set('history_months', e.target.value)} className={INPUT} inputMode="numeric" placeholder="e.g. 13" />
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Rollout note (shown when 12 months may not exist yet)</label>
                  <input value={draft.rollout_note} onChange={e => set('rollout_note', e.target.value)} className={INPUT} />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className={LABEL}>Download steps, one per line</label>
                  <textarea value={draft.download_steps} onChange={e => set('download_steps', e.target.value)} className={`${INPUT} min-h-[168px] font-mono text-xs`} data-testid="admin-utility-steps" />
                </div>
                <div>
                  <label className={LABEL}>Source page the steps were written from</label>
                  <input value={draft.instructions_source_url} onChange={e => set('instructions_source_url', e.target.value)} className={INPUT} placeholder="https://" />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input type="checkbox" checked={draft.instructions_verified} onChange={e => set('instructions_verified', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-purple-600" />
                  Steps verified (a homeowner confirmed them, or you did)
                </label>
                {u.instructions_feedback && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    <p className="font-medium">A homeowner said the steps differed:</p>
                    <p className="mt-1 whitespace-pre-wrap">{u.instructions_feedback}</p>
                    <button type="button" onClick={onClearFeedback} disabled={saving} className="mt-2 underline">
                      Clear once the steps are corrected
                    </button>
                  </div>
                )}
                <div>
                  <label className={LABEL}>Note shown to contractors under the utility</label>
                  <input value={draft.ami_notes} onChange={e => set('ami_notes', e.target.value)} className={INPUT} />
                </div>
                <div>
                  <label className={LABEL}>Internal notes</label>
                  <input value={draft.notes} onChange={e => set('notes', e.target.value)} className={INPUT} />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input type="checkbox" checked={draft.is_active} onChange={e => set('is_active', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-purple-600" />
                  Active
                </label>
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <button type="button" onClick={onSave} disabled={saving} data-testid="admin-utility-save" className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:bg-gray-300">
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={onCancel} disabled={saving} className="text-sm text-gray-600 underline">Close</button>
                {savedAt && !saveError && <span className="text-xs text-green-700">Saved.</span>}
                {saveError && <span className="text-xs text-red-700" data-testid="admin-utility-error">{saveError}</span>}
                {u.eia_id && <span className="text-xs text-gray-400 ml-auto">EIA {u.eia_id}</span>}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
